import { useEffect } from "react";
import { useFieldArray, useForm, type Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";

import { isApiError } from "@config/apiClient";
import { COMPANY_QUERY_KEY } from "@config/query";
import { useUpdateCompany } from "@screens/company/hooks/useUpdateCompany";
import {
  useAddAlternativeDomain,
  useDeleteAlternativeDomain,
  useUpdateAlternativeDomain,
} from "@screens/company/hooks/useAlternativeDomainMutations";
import { ICompany } from "@interfaces/company.interface";
import { removeHttpPrefix } from "@utils/strings.util";
import {
  findRemoveTarget,
  normalizeURLForBackend,
  showApiError,
  type RemoveTarget,
} from "../utils";
import {
  DomainsFormValues,
  websiteDomainsSchema,
} from "../WebsiteDomainsForm/websiteDomains.schema";

export const MAX_ALTERNATIVE_DOMAINS = 50;
const DUPLICATE_ACROSS_COMPANIES_ERROR = "This domain is already used by another company.";

type CompanyArg = Pick<
  ICompany,
  "id" | "title" | "website" | "alternative_domains" | "cross_team_last_matching_domains"
>;

function toFormValues(company: CompanyArg | undefined): DomainsFormValues {
  return {
    primary: removeHttpPrefix(company?.website ?? ""),
    alternatives: (company?.alternative_domains ?? []).map((value) => ({ value })),
  };
}

export function useWebsiteDomainsForm({ company }: { company: CompanyArg | undefined }) {
  const { mutateAsync: updatePrimaryAsync } = useUpdateCompany();
  const { mutateAsync: addAltAsync } = useAddAlternativeDomain();
  const { mutateAsync: updateAltAsync } = useUpdateAlternativeDomain();
  const { mutateAsync: deleteAltAsync } = useDeleteAlternativeDomain();

  const form = useForm<DomainsFormValues>({
    mode: "onBlur",
    resolver: yupResolver(websiteDomainsSchema) as Resolver<DomainsFormValues>,
    defaultValues: toFormValues(company),
  });
  const { register, getValues, setValue, reset, setError, trigger, formState } = form;
  const alternatives = useFieldArray({ control: form.control, name: "alternatives" });

  const isMutating = useIsMutating();
  const isFetchingCompany = useIsFetching({ queryKey: [COMPANY_QUERY_KEY, company?.id] });

  // Server is source of truth: resync once writes and the company refetch both settle, otherwise a
  // just-saved row flickers away (stale reset) and back.
  useEffect(() => {
    if (isMutating > 0 || isFetchingCompany > 0) return;
    reset(toFormValues(company));
  }, [company, isMutating, isFetchingCompany, reset]);

  const companyId = company?.id;
  const companyTitle = company?.title ?? "this company";
  const serverPrimary = removeHttpPrefix(company?.website ?? "");
  const serverAlts = company?.alternative_domains ?? [];
  const crossTeam = company?.cross_team_last_matching_domains ?? [];
  const hasUnsavedRow = alternatives.fields.length > serverAlts.length;
  const canAddMore = serverAlts.length < MAX_ALTERNATIVE_DOMAINS && !hasUnsavedRow;

  const addRow = () => alternatives.append({ value: "" });

  const discardUnsaved = () => reset(toFormValues(company));

  const commitAlternative = async (index: number) => {
    if (!companyId) return;
    if (!(await trigger(`alternatives.${index}.value`))) return;

    const normalized = normalizeURLForBackend(getValues(`alternatives.${index}.value`));
    if (!normalized) return;

    const existing = serverAlts[index]; // undefined = a new row (beyond the server list)
    if (normalized === existing) return;

    try {
      if (existing === undefined) {
        await addAltAsync({
          companyId,
          website: normalized,
          toast: `Domain ${normalized} added to ${companyTitle}`,
        });
      } else {
        await updateAltAsync({
          companyId,
          oldWebsite: existing,
          newWebsite: normalized,
          toast: `Domain ${normalized} updated for ${companyTitle}`,
        });
      }
    } catch (error) {
      if (isApiError(error) && error.response?.status === 400) {
        setError(`alternatives.${index}.value`, { message: DUPLICATE_ACROSS_COMPANIES_ERROR });
      } else {
        showApiError(error);
      }
    }
  };

  // → domain needing a change-primary confirmation, or null when invalid/unchanged.
  const preparePrimaryEdit = async (): Promise<string | null> => {
    if (!(await trigger("primary"))) return null;
    const normalized = normalizeURLForBackend(getValues("primary"));
    if (!normalized || normalized === serverPrimary) return null;
    return normalized;
  };

  const updatePrimaryDomain = async (value: string) => {
    if (!companyId) return;
    try {
      await updatePrimaryAsync({ id: companyId, website: `https://${value}` });
    } catch (error) {
      showApiError(error);
    }
  };

  const resetPrimary = () =>
    setValue("primary", serverPrimary, { shouldDirty: false, shouldValidate: false });

  // delete alt → PATCH primary → re-add old primary, with rollback (BE has no atomic swap).
  const promoteToPrimary = async (index: number) => {
    if (!companyId) return;
    const promoted = serverAlts[index];
    if (!promoted) return;

    try {
      await deleteAltAsync({ companyId, website: promoted });
    } catch (error) {
      showApiError(error);
      return;
    }
    try {
      await updatePrimaryAsync({ id: companyId, website: `https://${promoted}` });
    } catch {
      try {
        await addAltAsync({ companyId, website: promoted });
      } catch (rollbackError) {
        showApiError(rollbackError);
      }
      return;
    }
    if (serverPrimary) {
      try {
        await addAltAsync({ companyId, website: serverPrimary });
      } catch (error) {
        showApiError(error);
      }
    }
  };

  // Unsaved row → drop locally; saved row → domain + cross-team counterparts for the confirm.
  const resolveRemoveTarget = (index: number): RemoveTarget | null => {
    if (index >= serverAlts.length) {
      alternatives.remove(index);
      return null;
    }
    return findRemoveTarget(serverAlts, crossTeam, index);
  };

  const deleteAlternative = async (domain: string) => {
    if (!companyId) return;
    try {
      await deleteAltAsync({
        companyId,
        website: domain,
        toast: `Domain ${domain} removed from ${companyTitle}`,
      });
    } catch (error) {
      showApiError(error);
    }
  };

  return {
    register,
    formState,
    fields: alternatives.fields,
    badgeCount: serverAlts.length,
    canAddMore,
    addRow,
    discardUnsaved,
    commitAlternative,
    preparePrimaryEdit,
    updatePrimaryDomain,
    resetPrimary,
    promoteToPrimary,
    resolveRemoveTarget,
    deleteAlternative,
  };
}
